import React, { useState, useRef, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from 'recharts';
import './CRSTrendChart.css';

const CRSTrendChart = ({ crsHistory }) => {
  const [hoveredDate, setHoveredDate] = useState(null);
  const [lineAngle, setLineAngle] = useState(0);
  const chartRef = useRef(null);

  // Calculate the angle of the line by analyzing the SVG path
  useEffect(() => {
    const calculateAngle = () => {
      if (chartRef.current) {
        const svg = chartRef.current.querySelector('svg');
        if (svg) {
          const paths = svg.querySelectorAll('path[stroke="#572AF1"]');
          if (paths.length > 0) {
            const path = paths[0];
            const pathLength = path.getTotalLength();
            
            // Get the very last point
            const endPoint = path.getPointAtLength(pathLength - 1);
            // Use a smaller distance to get more precise angle at the end
            const prevPoint = path.getPointAtLength(Math.max(0, pathLength - 15));
            
            const dx = endPoint.x - prevPoint.x;
            const dy = endPoint.y - prevPoint.y;
            const angle = Math.atan2(dy, dx) * (180 / Math.PI);
            
            setLineAngle(angle);
          }
        }
      }
    };
    
    // Calculate immediately and recalculate on resize
    calculateAngle();
    const timer = setTimeout(calculateAngle, 100);
    window.addEventListener('resize', calculateAngle);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', calculateAngle);
    };
  }, [crsHistory]);

  // Early returns must be after all hooks
  if (!crsHistory || crsHistory.length === 0) {
    return <div className="crs-trend-empty">-</div>;
  }

  // Get last 7 scores (reversed for chronological order left to right)
  const scores = crsHistory.slice(0, 7).reverse();

  if (scores.length === 0) {
    return <div className="crs-trend-empty">-</div>;
  }

  // Format data for Recharts LineChart
  const chartData = scores.map((s, idx) => {
    const date = new Date(s.createdAt);
    const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return {
      name: new Date(s.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      value: s.crsScore,
      fullDate: dateStr,
      fullTime: timeStr,
      idx: idx
    };
  });

  // Calculate min and max for dynamic range
  const values = scores.map(s => s.crsScore);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const padding = (maxValue - minValue) * 0.1;
  const yMin = Math.max(0, minValue - padding);
  const yMax = Math.min(10, maxValue + padding);

  return (
    <div className="crs-trend-chart" ref={chartRef}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart 
          data={chartData} 
          margin={{ top: 5, right: 40, left: -20, bottom: 5 }}
          onMouseMove={(state) => {
            if (state && state.isTooltipActive && state.activePayload && state.activePayload.length) {
              const { value, fullDate, fullTime } = state.activePayload[0].payload;
              setHoveredDate({ crs: value.toFixed(2), date: fullDate, time: fullTime });
            }
          }}
          onMouseLeave={() => setHoveredDate(null)}
        >
          <CartesianGrid strokeDasharray="0" stroke="none" vertical={false} horizontal={false} />
          <XAxis dataKey="name" hide={true} />
          <YAxis hide={true} domain={[yMin, yMax]} />
          <Tooltip
            contentStyle={{ display: 'none' }}
            cursor={false}
          />
          <Line
            type="linear"
            dataKey="value"
            stroke="#572AF1"
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
          {chartData.length > 0 && (
            <Line
              type="linear"
              dataKey="value"
              stroke="none"
              strokeWidth={0}
              dot={(props) => {
                const { cx, cy, index } = props;
                if (index === chartData.length - 1) {
                  return (
                    <g transform={`translate(${cx}, ${cy}) rotate(${lineAngle})`}>
                      <polygon
                        points="0,-4 0,4 8,0"
                        fill="#572AF1"
                        stroke="#572AF1"
                        strokeWidth="0.5"
                      />
                    </g>
                  );
                }
                return null;
              }}
              isAnimationActive={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {hoveredDate && (
        <span className="crs-trend-tooltip">
          <div>CRS: {hoveredDate.crs}</div>
          <div>{hoveredDate.date}</div>
          <div>{hoveredDate.time}</div>
        </span>
      )}
    </div>
  );
};

export default CRSTrendChart;
